import React, { useRef, useState } from 'react';
import axios from 'axios';
import * as Popover from '@radix-ui/react-popover';
import './PriceCalculator.css';
import { numberSeparators, toInputText, formatNumber } from './numberFormat';

// 'DEC' is the decimal key — shown and typed as the company Number
// Format's decimal separator (. or ,).
const KEYS = ['7', '8', '9', '÷', '4', '5', '6', '×', '1', '2', '3', '−', 'BACK', '0', 'DEC', '+'];
const SYMBOL_MAP = { '÷': '/', '×': '*', '−': '-' };

// Grid key sits where users naturally reach after a mis-typed digit, so it's
// backspace (removes one character) rather than a second full Clear — the
// standalone "C" beside Tax+/Tax− covers clearing the whole expression.
function BackspaceIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  );
}

// Recursive-descent parser for +, -, *, /, parentheses and decimals — not
// eval(), since expr is arbitrary user-typed text landing straight in a
// price field. tokens/pos threaded through by hand (no lexer/parser lib
// pulled in for four operators).
function tokenize(expr) {
  const tokens = [];
  const re = /\s*([0-9]*\.?[0-9]+|[()+\-*/])\s*/y;
  let i = 0;
  while (i < expr.length) {
    re.lastIndex = i;
    const m = re.exec(expr);
    if (!m || m[0].length === 0) throw new Error('Invalid expression');
    tokens.push(m[1]);
    i = re.lastIndex;
  }
  return tokens;
}

function parseExpr(tokens, pos) {
  let { value, pos: p } = parseTerm(tokens, pos);
  while (tokens[p] === '+' || tokens[p] === '-') {
    const op = tokens[p];
    const rhs = parseTerm(tokens, p + 1);
    value = op === '+' ? value + rhs.value : value - rhs.value;
    p = rhs.pos;
  }
  return { value, pos: p };
}

function parseTerm(tokens, pos) {
  let { value, pos: p } = parseFactor(tokens, pos);
  while (tokens[p] === '*' || tokens[p] === '/') {
    const op = tokens[p];
    const rhs = parseFactor(tokens, p + 1);
    if (op === '/' && rhs.value === 0) throw new Error('Divide by zero');
    value = op === '*' ? value * rhs.value : value / rhs.value;
    p = rhs.pos;
  }
  return { value, pos: p };
}

function parseFactor(tokens, pos) {
  const tok = tokens[pos];
  if (tok === undefined) throw new Error('Invalid expression');
  if (tok === '-') {
    const inner = parseFactor(tokens, pos + 1);
    return { value: -inner.value, pos: inner.pos };
  }
  if (tok === '+') return parseFactor(tokens, pos + 1);
  if (tok === '(') {
    const inner = parseExpr(tokens, pos + 1);
    if (tokens[inner.pos] !== ')') throw new Error('Missing )');
    return { value: inner.value, pos: inner.pos + 1 };
  }
  const num = Number(tok);
  if (Number.isNaN(num)) throw new Error('Invalid expression');
  return { value: num, pos: pos + 1 };
}

// The display is in the company Number Format (a decimal comma for
// 1.234,56), so its decimal separator becomes '.' and any thousands
// separator is dropped before parsing. In 1 234,56 a '.' typed from the
// keyboard counts as the decimal too.
function toPlainExpression(expr) {
  const { group, decimal } = numberSeparators();
  if (decimal === '.') return expr.split(group).join('');
  let s = expr.replace(/[\s\u00A0\u202F]/g, '');
  if (group === '.') s = s.replace(/\./g, '');
  return s.replace(/,/g, '.');
}

function evaluateExpression(expr) {
  const tokens = tokenize(toPlainExpression(expr));
  if (tokens.length === 0) throw new Error('Invalid expression');
  const { value, pos } = parseExpr(tokens, 0);
  if (pos !== tokens.length) throw new Error('Invalid expression');
  return value;
}

// Small popup calculator that types/clicks an arithmetic expression and
// hands the evaluated result to onResult on "=" — the same trigger-button-
// beside-a-field shape FieldHelp.js uses for its "?" popover, applied to
// computing a value instead of explaining one.
function PriceCalculator({ initialValue, onResult }) {
  const [expr, setExpr] = useState('');
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  // undefined = not fetched yet, null = fetched but no default Tax Code —
  // fetched lazily on first open rather than on mount, since this
  // component is instantiated once per price row (see
  // PricePricingPage.js) and most rows' calculators are never
  // opened at all.
  const [taxRate, setTaxRate] = useState(undefined);
  const taxRateRequested = useRef(false);

  function press(key) {
    setError(null);
    if (key === 'C') { setExpr(''); return; }
    if (key === 'BACK') { setExpr((e) => e.slice(0, -1)); return; }
    if (key === 'DEC') { setExpr((e) => e + numberSeparators().decimal); return; }
    setExpr((e) => e + (SYMBOL_MAP[key] || key));
  }

  function commit() {
    if (!expr.trim()) return;
    try {
      const value = evaluateExpression(expr);
      if (!Number.isFinite(value)) throw new Error('Invalid result');
      onResult(Math.round(value * 10000) / 10000);
      setExpr('');
      setOpen(false);
    } catch (err) {
      setError('Invalid expression');
    }
  }

  // Tax+/Tax− evaluate the current expression and replace the display with
  // the GST-adjusted figure (same "apply, keep editing" shape as a physical
  // calculator's tax keys) rather than committing straight to the field —
  // so a miskeyed source value is still visible/correctable before "=".
  // The result is rounded to 4 decimals, half up — the most a price field
  // holds — so the figure shown is exactly the one "=" puts in the field,
  // and taking GST off doesn't lose the cents a line Amount needs.
  function applyTax(direction) {
    setError(null);
    if (!expr.trim() || !taxRate) return;
    try {
      const value = evaluateExpression(expr);
      const factor = 1 + taxRate / 100;
      const result = direction > 0 ? value * factor : value / factor;
      if (!Number.isFinite(result)) throw new Error('Invalid result');
      setExpr(toInputText(Math.round(result * 10000 + Number.EPSILON) / 10000));
    } catch (err) {
      setError('Invalid expression');
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') { e.preventDefault(); commit(); }
  }

  return (
    <Popover.Root
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        setError(null);
        // Opening seeds the display with the field's current value, so a
        // click straight into an operator (e.g. "*1.1") adjusts it in place
        // rather than starting from a blank calculator.
        setExpr(o ? toInputText(initialValue) : '');
        if (o && !taxRateRequested.current) {
          taxRateRequested.current = true;
          axios.get('/api/tax-codes')
            .then((res) => {
              const defaultCode = (res.data.data || []).find((t) => t.is_default);
              setTaxRate(defaultCode ? Number(defaultCode.rate) : null);
            })
            .catch(() => setTaxRate(null));
        }
      }}
    >
      <Popover.Trigger asChild>
        <button type="button" className="price-calc-trigger" aria-label="Price calculator">=</button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className="price-calc-content" sideOffset={8} align="end">
          <input
            type="text"
            inputMode="decimal"
            className="price-calc-display"
            value={expr}
            onChange={(e) => { setExpr(e.target.value); setError(null); }}
            onKeyDown={handleKeyDown}
            onFocus={(e) => e.target.select()}
            placeholder="0"
            autoFocus
          />
          {error && <div className="price-calc-error">{error}</div>}
          <div className="price-calc-tax-keys">
            <button
              type="button"
              className="price-calc-key price-calc-key-clear"
              onClick={() => press('C')}
            >
              C
            </button>
            <button
              type="button"
              className="price-calc-key price-calc-tax-key"
              onClick={() => applyTax(1)}
              disabled={!taxRate}
              title={taxRate ? `Add ${formatNumber(taxRate, { maxDecimals: 4 })}% GST` : 'No default Tax Code set'}
            >
              Tax+
            </button>
            <button
              type="button"
              className="price-calc-key price-calc-tax-key"
              onClick={() => applyTax(-1)}
              disabled={!taxRate}
              title={taxRate ? `Remove ${formatNumber(taxRate, { maxDecimals: 4 })}% GST` : 'No default Tax Code set'}
            >
              Tax−
            </button>
          </div>
          <div className="price-calc-keys">
            {KEYS.map((key) => (
              <button
                key={key}
                type="button"
                className="price-calc-key"
                aria-label={key === 'BACK' ? 'Backspace' : (key === 'DEC' ? 'Decimal point' : undefined)}
                onClick={() => press(key)}
              >
                {key === 'BACK' ? <BackspaceIcon /> : (key === 'DEC' ? numberSeparators().decimal : key)}
              </button>
            ))}
          </div>
          <button type="button" className="price-calc-equals" onClick={commit}>=</button>
          <Popover.Arrow className="price-calc-arrow" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export default PriceCalculator;
